import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import heroBg from "../assets/images/hero-bg-img.png"; // use any good hero image
import logoBCT from "../assets/images/logoBT.png";
import SEO from "../components/seo/SEO";
import ShareActions from "../components/common/ShareActions";

const About = () => {
  return (
    <Box>
      <SEO
        title="About NearbyGuide | City-based Local Discovery Platform"
        description="NearbyGuide is a city-based local discovery platform powered by Oglitz Software Pvt Ltd, helping users find trusted places near them."
        canonical="https://nearbyguide.in/about"
        image="https://nearbyguide.in/images/og-about-nearbyguide.png"
      />

      {/* ================= HERO SECTION ================= */}
      <Box
        sx={{
          position: "relative",
          height: { xs: 260, md: 360 },
          display: "flex",
          alignItems: "center",
          backgroundImage: `
            linear-gradient(
              90deg,
              rgba(13,59,102,0.85),
              rgba(13,59,102,0.45)
            ),
            url(${heroBg})
          `,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 600 }}>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: "2rem", md: "2.8rem" },
                fontWeight: 900,
                color: "#fff",
                mb: 1,
              }}
            >
              About NearbyGuide
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "1rem", md: "1.1rem" },
                color: "rgba(255,255,255,0.9)",
                lineHeight: 1.7,
              }}
            >
              Discover trusted nearby places across cities — fast, simple, and
              reliable.
            </Typography>
          </Box>
          <ShareActions
            title="About NearbyGuide"
            text="NearbyGuide helps you discover trusted places and services near you."
          />
        </Container>
      </Box>

      {/* ================= CONTENT SECTION ================= */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* INTRO */}
        <Typography
          sx={{
            fontSize: "1.1rem",
            color: "var(--text-primary)",
            lineHeight: 1.9,
            maxWidth: 900,
            mb: 5,
          }}
        >
          <strong>NearbyGuide</strong> is a city-wise local discovery platform
          built to help people quickly find essential and popular places such as
          temples, hospitals, schools, colleges, water parks, tourist spots,
          shopping malls, restaurants, and more — all in one clean interface.
        </Typography>

        {/* ================= CARDS ================= */}
        <Grid container spacing={4}>
          {/* WHAT WE DO */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%", borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  sx={{
                    fontSize: "1.4rem",
                    fontWeight: 800,
                    color: "var(--text-heading)",
                    mb: 1,
                  }}
                >
                  What NearbyGuide Does
                </Typography>

                <Typography
                  sx={{ color: "var(--text-primary)", lineHeight: 1.8 }}
                >
                  NearbyGuide organizes local place information city-wise,
                  making it easy to explore nearby locations without confusion
                  or clutter. Our focus is on clarity, speed, and usability.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* WHY IT EXISTS */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%", borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  sx={{
                    fontSize: "1.4rem",
                    fontWeight: 800,
                    color: "var(--text-heading)",
                    mb: 1,
                  }}
                >
                  Why NearbyGuide Exists
                </Typography>

                <Typography
                  sx={{ color: "var(--text-primary)", lineHeight: 1.8 }}
                >
                  Searching for local places shouldn’t be complicated.
                  NearbyGuide exists to remove noise and help users find what
                  they need — quickly, accurately, and without unnecessary
                  distractions.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ================= POWERED BY ================= */}
        <Box
          sx={{
            mt: 6,
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            backgroundColor: "#fff",
            boxShadow: "0 20px 50px rgba(13,59,102,0.12)",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: 4,
          }}
        >
          {/* LOGO */}
          <Box>
            <Box
              component="img"
              src={logoBCT}
              alt="Oglitz Software Pvt Ltd"
              sx={{
                width: { xs: 160, md: 200 },
              }}
            />
          </Box>

          {/* TEXT */}
          <Box>
            <Typography
              sx={{
                fontSize: "1.5rem",
                fontWeight: 900,
                color: "var(--text-heading)",
                mb: 1,
              }}
            >
              Powered by Oglitz Software Pvt. Ltd.
            </Typography>

            <Typography sx={{ color: "var(--text-primary)", lineHeight: 1.8 }}>
              NearbyGuide is built and maintained by{" "}
              <strong>Oglitz Software Pvt. Ltd.</strong>, a technology company
              focused on building scalable, performance-driven web applications
              and SaaS platforms.
            </Typography>

            <Typography
              sx={{
                color: "var(--text-primary)",
                lineHeight: 1.8,
                mt: 1,
              }}
            >
              Oglitz specializes in custom software development, UI/UX design,
              SaaS products, and modern digital solutions that help businesses
              grow with confidence.
            </Typography>

            <Typography
              sx={{
                mt: 1.5,
                fontSize: "0.95rem",
                color: "var(--text-muted)",
              }}
            >
              Visit: <strong>oglitz.com</strong>
            </Typography>
          </Box>
        </Box>

        {/* ================= FOOTER NOTE ================= */}
        <Typography
          sx={{
            textAlign: "center",
            mt: 6,
            fontSize: "0.9rem",
            color: "var(--text-muted)",
          }}
        >
          © {new Date().getFullYear()} NearbyGuide — Powered by Oglitz Software
          Pvt. Ltd.
        </Typography>
      </Container>
    </Box>
  );
};

export default About;
